import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../services/useAuth";
import { fetchApprovalList } from "../approvals/api";
import { getApprovalPlugin, getStageConfig } from "../approvals/registry";
import { filterItemsByApprovalAccess } from "../config/approvalAccess";

/** Queue items carry requestedAt/resolvedAt only — no createdAt/updatedAt. */
const approvalItemTime = (item) => {
  const raw =
    item?.status === "pending"
      ? item?.requestedAt
      : item?.resolvedAt || item?.requestedAt;
  const time = raw ? new Date(raw).getTime() : 0;
  return Number.isNaN(time) ? 0 : time;
};

/**
 * Admin approval inbox: queue/history fetch + plugin-driven approve/reject.
 */
export function useApprovalInbox({
  stage = "admin",
  canWrite = false,
  canWriteEntity,
  approvalUser = null,
  listMode = "pending",
  queueType = "all",
  queueParams = {},
  onNotify,
}) {
  const canWriteForItem = useCallback(
    (item) => {
      const type = item?.type || item?.entityType;
      const assignedByWorkflow = (item?.approverUserIds || []).some(
        (id) => String(id) === String(approvalUser?._id || approvalUser?.id),
      );
      if (assignedByWorkflow) return true;
      if (typeof canWriteEntity === "function") {
        return canWriteEntity(type);
      }
      return !!canWrite;
    },
    [canWrite, canWriteEntity, approvalUser],
  );
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewItem, setViewItem] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const readOnly = listMode === "history";

  const canActOnItem = useCallback(
    (item) => {
      if (readOnly) return false;
      if (listMode === "all") return item?.status === "pending";
      const stageConfig = getStageConfig(item, stage);
      return stageConfig?.canActOn?.(item) ?? false;
    },
    [readOnly, listMode, stage],
  );

  const callbacksRef = useRef({ onNotify });
  callbacksRef.current = { onNotify };

  const queueParamsKey = JSON.stringify({ ...queueParams, listMode });
  const loadingKey = (id, action) => `${id}_${action}`;

  const getPlugin = (item) => getApprovalPlugin(item?.type);

  const getEntityId = (item) => {
    const plugin = getPlugin(item);
    return plugin?.getId?.(item) ?? item?.entityId ?? item?.id;
  };

  const isActionLoading = useCallback(
    (item, action) => actionLoading === loadingKey(getEntityId(item), action),
    [actionLoading],
  );

  const fetchIdRef = useRef(0);

  const refetch = useCallback(async () => {
    const currentFetchId = ++fetchIdRef.current;
    try {
      setLoading(true);
      const params = JSON.parse(queueParamsKey);
      const { items: next } = await fetchApprovalList(getToken(), {
        type: queueType,
        listMode,
        stage,
        ...params,
      });
      
      if (currentFetchId !== fetchIdRef.current) return;

      const filtered = approvalUser
        ? filterItemsByApprovalAccess(approvalUser, next)
        : next;
      const sorted = [...filtered].sort((a, b) => {
        const aPending = a.status === "pending";
        const bPending = b.status === "pending";
        if (aPending && !bPending) return -1;
        if (!aPending && bPending) return 1;
        return approvalItemTime(b) - approvalItemTime(a);
      });
      setItems(sorted);
    } catch (err) {
      if (currentFetchId !== fetchIdRef.current) return;
      callbacksRef.current.onNotify?.(
        err.response?.data?.message || "Onay listesi yüklenemedi",
        "danger",
      );
      setItems([]);
    } finally {
      if (currentFetchId === fetchIdRef.current) {
        setLoading(false);
      }
    }
  }, [getToken, queueType, queueParamsKey, listMode, stage, approvalUser]);

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueType, queueParamsKey, listMode, stage, approvalUser?._id]);

  const approve = useCallback(
    async (item, options = {}) => {
      if (!canWriteForItem(item) || readOnly || !item || !canActOnItem(item))
        return null;
      const stageConfig = getStageConfig(item, stage);
      if (!stageConfig) return null;
      const id = getEntityId(item);
      if (!id) return null;

      try {
        setActionLoading(loadingKey(id, "approve"));
        const data = await stageConfig.approve(item, {
          token: getToken(),
          attachment: options.attachment,
        });
        if (data?.success !== false) {
          callbacksRef.current.onNotify?.(
            data?.message || stageConfig.messages.approveSuccess,
            "success",
          );
          await stageConfig.onApproveSuccess?.(data, item, { navigate });
          setViewItem(null);
          await refetch();
          return data;
        }
        callbacksRef.current.onNotify?.(
          data?.message || "Onay başarısız",
          "danger",
        );
        return null;
      } catch (err) {
        callbacksRef.current.onNotify?.(
          err.response?.data?.message || "Onay başarısız",
          "danger",
        );
        return null;
      } finally {
        setActionLoading(null);
      }
    },
    [canWriteForItem, readOnly, stage, getToken, refetch, navigate, canActOnItem],
  );

  const startReject = useCallback((item) => {
    setRejectTarget(item);
    setRejectReason("");
  }, []);

  const cancelReject = useCallback(() => {
    setRejectTarget(null);
    setRejectReason("");
  }, []);

  const confirmReject = useCallback(async () => {
    if (
      !canWriteForItem(rejectTarget) ||
      readOnly ||
      !rejectTarget ||
      !canActOnItem(rejectTarget)
    )
      return null;
    const stageConfig = getStageConfig(rejectTarget, stage);
    if (!stageConfig) return null;
    const id = getEntityId(rejectTarget);
    if (!id) return null;

    try {
      setActionLoading(loadingKey(id, "reject"));
      const data = await stageConfig.reject(rejectTarget, rejectReason, {
        token: getToken(),
      });
      if (data?.success !== false) {
        callbacksRef.current.onNotify?.(
          stageConfig.messages.rejectSuccess,
          "success",
        );
        cancelReject();
        setViewItem(null);
        await refetch();
        return data;
      }
      callbacksRef.current.onNotify?.(
        data?.message || "Red işlemi başarısız",
        "danger",
      );
      return null;
    } catch (err) {
      callbacksRef.current.onNotify?.(
        err.response?.data?.message || "Red işlemi başarısız",
        "danger",
      );
      return null;
    } finally {
      setActionLoading(null);
    }
  }, [
    canWriteForItem,
    readOnly,
    rejectTarget,
    rejectReason,
    stage,
    getToken,
    cancelReject,
    refetch,
    canActOnItem,
  ]);

  return {
    items,
    loading,
    refetch,
    listMode,
    readOnly,
    canActOnItem,
    canWriteForItem,
    viewItem,
    setViewItem,
    approve,
    startReject,
    cancelReject,
    confirmReject,
    rejectTarget,
    rejectReason,
    setRejectReason,
    isActionLoading,
    getPlugin,
    getStageConfig: (item) => getStageConfig(item, stage),
  };
}
